import React, { useMemo, useState, useCallback } from "react";
import { dateOnlyAsNumbers, timeOnlyFormatter } from '../../../utils/dateFormatter'
import { recordLengthFormatter } from '../../../utils/recordLengthFormatter'


import TableFull from '../CommonTable/TableFull'


import styles from './RecordsListTable.module.scss';
import { Button, Dropdown, Form, Row, Col } from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import * as CommonIcons from '../../CommonIcons';

function Filter({
  column,
  table,
}) {
  const { t } = useTranslation();

  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id)

  return typeof firstValue === 'number' ? (
    <div className="d-flex space-x-2">
      <input
        type="number"
        value={column.getFilterValue()}
        onChange={e =>
          column.setFilterValue((old) => [e.target.value, old?.[1]])
        }
        placeholder={`Min`}
        className="w-24 border shadow rounded"
      />
      <input
        type="number"
        value={((column.getFilterValue())?.[1] ?? '')}
        onChange={e =>
          column.setFilterValue((old) => [old?.[0], e.target.value])
        }
        placeholder={`Max`}
        className="w-24 border shadow rounded"
      />
    </div>
  ) : (
    <input
      type="text"
      value={(column.getFilterValue() ?? '')}
      onChange={e => column.setFilterValue(e.target.value.trim())}
      placeholder={`${t('common.table.search.prompt')}...`}
      className="w-36 border shadow rounded"
    />
  )
}

//Working example: https://tanstack.com/table/v8/docs/examples/react/row-selection

export default function RecordsListTable({ data, onRecordClick, resetSelection, onEditRecordClick, onMoveRecordClick, onDeleteRecordClick, onEditEcg12, onEditTrends }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const searchFromUrl = new URLSearchParams(location.search).get('search') || "";
  const [globalFilter, setGlobalFilter] = useState(searchFromUrl);

  const updateSearch = (value) => {
    setGlobalFilter(value);
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.delete('page');
    navigate({ search: params.toString() }, { replace: true });
  };

  const formattedGlobalFilter = useCallback((row, columnId, filterValue) => {
    const search = filterValue.toLowerCase();
    let value;
    if (columnId === 'date-date') {
      value = dateOnlyAsNumbers(row.getValue(columnId));
    } else if (columnId === 'date-time') {
      value = timeOnlyFormatter(row.getValue(columnId));
    } else {
      value = row.getValue(columnId);
    }
    return String(value ?? '').toLowerCase().includes(search);
  }, []);

  const urlParams = new URLSearchParams(location.search);
  const [filterEcg12, setFilterEcg12] = useState(onEditEcg12 !== null && urlParams.get('ecg12') === '1');
  const [filterTrends, setFilterTrends] = useState(onEditTrends !== null && urlParams.get('trends') === '1');

  const updateFilter = (ecg12, trends) => {
    setFilterEcg12(ecg12);
    setFilterTrends(trends);
    const params = new URLSearchParams(location.search);
    if (ecg12) params.set('ecg12', '1'); else params.delete('ecg12');
    if (trends) params.set('trends', '1'); else params.delete('trends');
    params.delete('page');
    navigate({ search: params.toString() }, { replace: true });
  };

  const filteredData = useMemo(() => {
    if (!filterEcg12 && !filterTrends) return data;
    return data.filter(record => {
      if (filterEcg12 && filterTrends) return record.canBeUsedForEcg12 && record.canBeIncludedInTrends;
      if (filterEcg12) return record.canBeUsedForEcg12;
      return record.canBeIncludedInTrends;
    });
  }, [data, filterEcg12, filterTrends]);

  const hasActiveFilter = filterEcg12 || filterTrends;
  const showFilterDropdown = onEditEcg12 !== null || onEditTrends !== null;

  const [showOperationsColumn] = React.useState(onMoveRecordClick !== null || onDeleteRecordClick !== null || onEditEcg12 !== null || onEditTrends !== null);

  const onRecordClickHandler = (record) => {
    console.log(record);
    onRecordClick(record);
  //  navigate(`/user/${userUid}/record/${record.uid}`);
  };

  const columnArray =
    [
      //{
      //  header: "#",
      //  className: styles.checkCell,
      //  enableColumnFilter: false,
      //  cell: ({ row }) => (
      //    <div>
      //      {row.index+1}
      //    </div>
      //  )
      //},
      {
        header: t('recordList.table.serial'),
        accessorKey: "serialNumber",
        enableGlobalFilter: true,
        cell: info => {
          return (
            <>
              {info.row.original.realTimeState === 10 && <i className={`bi bi-circle-fill ${styles.waitingDataCircle}`}></i>}
              {info.row.original.realTimeState === 20 && <i className={`bi bi-circle-fill ${styles.liveDataCircle}`}></i>}
              <span className="ps-3">{info.getValue()}</span>
              {/* <Button variant="link" onClick={() => onRecordClickHandler(info.row.original)}>{info.getValue()}</Button> */}
            </>
          )
        },
        allowWrap: true,
        meta: {
          getCellContextInner: (context) => {            
            //console.log(context);            
              return {
                style: { textWrap: 'nowrap'}
              }            
          }
        }
      },
      {
        id: 'date-date',
        header: t('recordList.table.date'),
        accessorKey: "date",
        enableColumnFilter: false,
        enableGlobalFilter: true,
        cell: props => <>{dateOnlyAsNumbers(props.getValue())}</>
      },
      {
        id: 'date-time',
        header: t('recordList.table.time'),
        accessorKey: "date",
        enableColumnFilter: false,
        enableGlobalFilter: true,
        cell: props => <>{timeOnlyFormatter(props.getValue())}</>
      },
      {
        header: t('recordList.table.length'),
        accessorKey: "recordLengthSeconds",
        enableColumnFilter: false,
        enableGlobalFilter: false,
        cell: props => <span>{recordLengthFormatter(props.getValue())}</span>,
      },
      {
        header: t('recordList.table.recordType'),
        accessorKey: "recordTypeName",
        enableColumnFilter: false,
        enableGlobalFilter: true,
      },
      {
        header: t('recordList.table.branch'),
        accessorKey: "branch",
        enableColumnFilter: false,
        enableGlobalFilter: true,
      },
      {
        header: t('recordList.table.recordName'),
        accessorKey: "recordName",
        enableGlobalFilter: true,
      },
      {
        header: t('recordList.table.recordPlace'),
        accessorKey: "place",
        enableGlobalFilter: true,
      },
      {
        header: t('recordList.table.description'),
        accessorKey: "description",
        allowWrap: true,
        enableGlobalFilter: true,
      }      
    ];


  const ShowInEcg12Button = ({ row }) => {
    const [value, setValue] = useState(row.isShowInEcg12);
    const [canBeChanged] = useState(row.canBeUsedForEcg12);

    const onClick = (isShow) => {
      setValue(isShow);
      row.isShowInEcg12 = isShow;      

      onEditEcg12(row, callbackResult);
    };

    const callbackResult = (data) => {      
      if (!data)
        setValue(false);
    };

    return (
      <>
        <div className={styles.actionButton}>
          {canBeChanged && <>
            {
              !value &&
              <Button className={styles.btnHoverable} variant="" onClick={(e) => { e.stopPropagation(); onClick(true); }} title={t('recordList.actions.addToEcg12')}>
                <span className={styles.btnNoHover}><CommonIcons.AddToEcg12 className={styles.icon} /></span><span className={styles.btnHover}><CommonIcons.AddToEcg12Hover className={styles.icon} /></span>
              </Button>
            }
            {
              value &&
              <Button className={styles.btnHoverable} variant="" onClick={(e) => { e.stopPropagation(); onClick(false); }} title={t('recordList.actions.removeFromEcg12')} >
                <span className={styles.btnNoHover}><CommonIcons.RemoveFromEcg12 className={styles.icon} /></span><span className={styles.btnHover}><CommonIcons.RemoveFromEcg12Hover className={styles.icon} /></span>
              </Button>
            }
          </>
          }
          {!canBeChanged &&
            <Button variant="" onClick={(e) => { e.stopPropagation() }} title={t('recordList.actions.cannotAddToEcg12')}>
              <CommonIcons.AddToEcg12Disabled className={styles.icon} />
            </Button>
          }
        </div>        
      </>
    )
  };

  const IncludeInTrendsButton = ({ row }) => {
    const [value, setValue] = useState(row.includedInTrends);
    const [canBeChanged] = useState(row.canBeIncludedInTrends);

    const onClick = (include) => {
      const previousValue = value;
      setValue(include);
      row.includedInTrends = include;
      onEditTrends(row, (success) => {
        if (!success) {
          setValue(previousValue);
          row.includedInTrends = previousValue;
        }
      });
    };

    return (
      <>
        <div className={styles.actionButton}>
          {canBeChanged && <>
            {
              !value &&
              <Button className={styles.btnHoverable} variant="" onClick={(e) => { e.stopPropagation(); onClick(true); }} title={t('recordList.actions.addToTrends')}>
                <span className={styles.btnNoHover}><CommonIcons.AddToTrends className={styles.icon} /></span><span className={styles.btnHover}><CommonIcons.AddToTrendsHover className={styles.icon} /></span>
              </Button>
            }
            {
              value &&
              <Button className={styles.btnHoverable} variant="" onClick={(e) => { e.stopPropagation(); onClick(false); }} title={t('recordList.actions.removeFromTrends')}>
                <span className={styles.btnNoHover}><CommonIcons.RemoveFromTrends className={styles.icon} /></span><span className={styles.btnHover}><CommonIcons.RemoveFromTrendsHover className={styles.icon} /></span>
              </Button>
            }
          </>
          }
          {!canBeChanged &&
            <Button variant="" onClick={(e) => { e.stopPropagation() }} title={t('recordList.actions.cannotAddToTrends')}>
              <CommonIcons.AddToTrendsDisabled className={styles.icon} />
            </Button>
          }
        </div>
      </>
    )
  };

  if (showOperationsColumn)
    columnArray.push(
      {
        id: 'operations',
        header: t('recordList.table.action'),
        hideOnMobile: true,
        accessorKey: "uid",
        enableGlobalFilter: false,
        show: false,
        cell: ({ row }) => (
          <div className={styles.actionButtons}>
            {onEditEcg12 &&
              <ShowInEcg12Button key={`ecg12-${row.original.uid}`} row={row.original} />
            }
            {onEditTrends &&
              <IncludeInTrendsButton key={`trends-${row.original.uid}`} row={row.original} />
            }
            {onEditRecordClick &&
              <div className={styles.actionButton}>
                <Button variant="" onClick={(e) => { e.stopPropagation(); onEditRecordClick(row.original); }} title={t('recordList.actions.edit')} >
                  <CommonIcons.PensilEdit className={styles.icon} />
                </Button>
              </div>
            }
            {onMoveRecordClick &&
              <div className={styles.actionButton}>
                <Button variant="" onClick={(e) => { e.stopPropagation(); onMoveRecordClick(row.original); }} title={t('recordList.actions.move')} >
                  <CommonIcons.MoveRecord className={styles.icon} />
                </Button>
              </div>
            }
            {onDeleteRecordClick &&
              <div className={styles.actionButton}>
                <Button variant="" onClick={(e) => { e.stopPropagation(); onDeleteRecordClick(row.original); }} title={t('recordList.actions.delete')}>
                  <CommonIcons.TrashCan className={styles.icon} />
                </Button>
              </div>
            }
          </div>
        )
      }
    );

  const columns = useMemo(
    () => columnArray,
    [t]
  );

  const initialStateSettings = {
    hiddenColumns: ['operations']

  };



  const dropDownMenuActions = [];
  if (onMoveRecordClick)
  {
    dropDownMenuActions.push({
      as: "button",
      onClick: row => onMoveRecordClick(row),
      text: t('recordList.actions.move')
    });
  };
  if (onDeleteRecordClick) {
    dropDownMenuActions.push({
      as: "button",
      onClick: row => onDeleteRecordClick(row),
      text: t('recordList.actions.delete')
    });
  }; 

  return (
    <>
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="auto" className="fw-bold">
          {t('recordList.search.prompt')}
        </Form.Label>
        <Col sm="8">
          <Form.Control
            type="text"
            value={globalFilter}
            onChange={(e) => updateSearch(e.target.value)}
            placeholder={t('recordList.search.placeholder')}
          />
        </Col>
        {showFilterDropdown &&
          <Col sm="auto">
            <Dropdown>
              <Dropdown.Toggle variant={hasActiveFilter ? "primary" : "outline-secondary"}>
                <i className={`bi ${hasActiveFilter ? 'bi-funnel-fill' : 'bi-funnel'}`}></i>
                {hasActiveFilter && <span className="ms-1">{t('recordList.filter.active')}</span>}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ whiteSpace: 'nowrap' }}>
                {onEditEcg12 !== null &&
                  <Dropdown.ItemText>
                    <Form.Check
                      type="checkbox"
                      id="filter-ecg12"
                      label={t('recordList.filter.ecg12')}
                      checked={filterEcg12}
                      onChange={(e) => updateFilter(e.target.checked, filterTrends)}
                    />
                  </Dropdown.ItemText>
                }
                {onEditTrends !== null &&
                  <Dropdown.ItemText>
                    <Form.Check
                      type="checkbox"
                      id="filter-trends"
                      label={t('recordList.filter.trends')}
                      checked={filterTrends}
                      onChange={(e) => updateFilter(filterEcg12, e.target.checked)}
                    />
                  </Dropdown.ItemText>
                }
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        }
      </Form.Group>

      <TableFull data={filteredData}
        columns={columns}
        styles={styles}
        onRowClick={onRecordClickHandler}
        dropDownMenuActions={dropDownMenuActions}
        resetSelection={resetSelection}
        initialStateSettings={initialStateSettings}
        mainTableClassName={showOperationsColumn ? styles.table : styles.tableNoOperations}
        allowGlobalFilter={true}
        globalFilterText={globalFilter}
        globalFilterFn={formattedGlobalFilter}
      />

    </>
  );
}


 //<p>Selected Rows: {Object.keys(selectedRowIds).length}</p>
 //     <pre>
 //       <code>
 //         {JSON.stringify(
 //           {
 //             selectedRowIds: selectedRowIds,
 //             'selectedFlatRows[].original': selectedFlatRows.map(
 //               d => d.original
 //             ),
 //           },
 //           null,
 //           2
 //         )}
 //       </code>
 //     </pre>